import { useEffect, useMemo } from "react";
import { fetchAPI, postAPI } from "../../../utils/utils";
import { useState } from "react";
import AuthorHistogram from "./AuthorHistogram";
import Ranking from "./Ranking";
import LastChanges from "./LastChanges";
import Pressable from "../../other/Pressable";
import UserInfo from "../../login/UserInfo";
import HeaderTabs from "../../interface/HeaderTabs";
import { getCastellerDisplayName, getCastellerNameWithMote } from "../../../utils/castellerName";

// Simple spinner component
const Spinner = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
    }}>
        <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        }}></div>
        <style>
            {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}
        </style>
    </div>
);

function BlameApp({ castellersInfo, setCastellersInfo, hideHeader = false, ...props }) {
    const [histogramData, setHistogramData] = useState(null);
    const [lastChangesRaw, setLastChangesRaw] = useState([]);
    const [loadingHistogram, setLoadingHistogram] = useState(false);
    const [loadingLastChanges, setLoadingLastChanges] = useState(false);
    const [nHores, setNHores] = useState(24);
    const [nLastCanvis, setNLastCanvis] = useState(200);
    const [bundles, setBundles] = useState([]);
    const [events, setEvents] = useState([]);

    const eventIds = useMemo(() => {
        const now = new Date();
        const threeMonthsAgo = new Date(now - 90 * 24 * 60 * 60 * 1000);
        return events
            .filter(event => {
                const end = new Date(event?.['data-esperada-fi']);
                return end >= threeMonthsAgo;
            })
            .map(event => event.id);
    }, [events]);

    // Parse last changes CSV into objects (memoized)
    const lastCanvis = useMemo(() => {
        return lastChangesRaw
            .map(log => log.split(','))
            .filter(([, , author, timestamp]) => timestamp && author !== 'anonymous')
            .map(([caixa, casteller, author, timestamp, actionId, eventId, filename]) => ({
                author,
                timestamp,
                caixa,
                casteller,
                actionId,
                eventId,
                filename,
            }))
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, [lastChangesRaw]);

    const ranking = histogramData?.ranking || {};

    useEffect(() => {
        fetchAPI('/bundles', setBundles);
    }, []);

    useEffect(() => {
        fetchAPI('/events', setEvents);
    }, []);

    // Fetch histogram data (re-fetches when nHores or eventIds change)
    useEffect(() => {
        if (eventIds.length === 0) return;
        setLoadingHistogram(true);
        postAPI('/get_blame_histogram', {
            events_ids: eventIds,
            nHours: nHores,
            maxBuckets: 200,
        }, (res) => {
            setHistogramData(res);
            setLoadingHistogram(false);
        });
    }, [eventIds.length, nHores]);

    // Fetch last changes (only when eventIds or nLastCanvis change)
    useEffect(() => {
        if (eventIds.length === 0) return;
        setLoadingLastChanges(true);
        postAPI('/get_positions_logs_fast', {
            events_ids: eventIds,
            limit: nLastCanvis,
        }, (res) => {
            setLastChangesRaw(res);
            setLoadingLastChanges(false);
        });
    }, [eventIds.length, nLastCanvis]);

    useEffect(() => {
        window.history.pushState({}, '', '/stats/blame');
    }, []);

    const displayName = user => user in castellersInfo ? getCastellerNameWithMote(castellersInfo[user]) : user
    const displayShortName = user => user in castellersInfo ? getCastellerDisplayName(castellersInfo[user]) : user

    return (
        <div
            style={{
                width: '90%',
            }}
        >
            {!hideHeader && (
                <>
                    <UserInfo {...props} />
                    <HeaderTabs {...props} />

                    <Pressable style={{ backgroundColor: '#eee' }} className="boto-back" href='/'>
                        ← Tornar a la pàgina principal
                    </Pressable>
                </>
            )}

            <h1>Historial de canvis</h1>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <h2>Últimes {nHores} hores</h2>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginLeft: 20,
                        gap: 5,
                        flexWrap: 'wrap',
                    }}
                >
                    <button onClick={() => setNHores(4)}>4h</button>
                    <button onClick={() => setNHores(8)}>8h</button>
                    <button onClick={() => setNHores(12)}>12h</button>
                    <button onClick={() => setNHores(24)}>24h</button>
                    <button onClick={() => setNHores(48)}>48h</button>
                    <button onClick={() => setNHores(72)}>72h</button>
                    <input
                        type="number"
                        min="1"
                        placeholder="Defineix hores"
                        onWheel={(event) => event.currentTarget.blur()}
                        onChange={(e) => parseInt(e.target.value) >= 1 ? setNHores(e.target.value) : undefined}
                    />
                </div>
            </div>

            {loadingHistogram ? (
                <Spinner />
            ) : (
                <>
                    <AuthorHistogram
                        histogramData={histogramData}
                        nHours={nHores}
                        castellersInfo={castellersInfo}
                        events={events}
                    />

                    <Ranking
                        ranking={ranking}
                        nHores={nHores}
                        displayName={displayShortName}
                    />
                </>
            )}

            {loadingLastChanges ? (
                <Spinner />
            ) : (
                <LastChanges
                    lastCanvis={lastCanvis}
                    setNLastCanvis={setNLastCanvis}
                    displayName={displayName}
                    bundles={bundles}
                    events={events}
                />
            )}

        </div>
    )
}

export default BlameApp;
